import { TextNode, type LexicalNode, type NodeKey, type SerializedTextNode, type Spread } from 'lexical';

export type SerializedMentionNode = Spread<
  { userId: string; mentionName: string },
  SerializedTextNode
>;

/**
 * An @-mention token in a Lexical document. Subclasses TextNode so the cursor
 * navigation / copy-paste / serialization machinery treats it like text, while
 * carrying a stable `userId` that survives round-trips through the editor state
 * and the exported HTML (`<span data-user-id="...">@Adam Jones</span>`).
 *
 * The trigger character is included in `__text` ("@Adam Jones") so the rendered
 * body matches what the user sees in the editor; the `userId` is what the
 * server uses to resolve the recipient.
 */
export class MentionNode extends TextNode {
  __userId: string;

  static getType(): string {
    return 'aes-mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__text, node.__userId, node.__key);
  }

  constructor(text: string, userId: string, key?: NodeKey) {
    super(text, key);
    this.__userId = userId;
  }

  static importJSON(json: SerializedMentionNode): MentionNode {
    const node = new MentionNode(json.mentionName, json.userId);
    node.setFormat(json.format);
    node.setDetail(json.detail);
    node.setMode(json.mode);
    node.setStyle(json.style);
    return node;
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      type: 'aes-mention',
      version: 1,
      userId: this.__userId,
      mentionName: this.__text
    };
  }

  createDOM(config: Parameters<TextNode['createDOM']>[0]): HTMLElement {
    const el = super.createDOM(config);
    el.setAttribute('data-user-id', this.__userId);
    // Inline tinted chip — matches Teams/Slack mention styling at a glance.
    el.classList.add(
      'rounded',
      'bg-primary/15',
      'text-primary',
      'px-1',
      'font-medium'
    );
    return el;
  }

  exportDOM(): { element: HTMLElement } {
    const el = document.createElement('span');
    el.setAttribute('data-user-id', this.__userId);
    el.className = 'rounded bg-primary/15 text-primary px-1 font-medium';
    el.textContent = this.__text;
    return { element: el };
  }

  /** Mentions behave atomically — typing inside doesn't split the token. */
  isTextEntity(): true {
    return true;
  }

  /** Don't merge with adjacent text — the mention boundary is meaningful. */
  canInsertTextBefore(): boolean {
    return false;
  }
  canInsertTextAfter(): boolean {
    return false;
  }
}

export function $createMentionNode(text: string, userId: string): MentionNode {
  const node = new MentionNode(text, userId);
  node.setMode('token'); // navigates as a single unit; backspace deletes whole mention
  return node;
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode;
}
